import { useState, useEffect, ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import { ModalShell, ModalHeader } from '../components/server/ServerModals';
import './BuyCredits.css';
import {
  CreditCard, Wallet, Calculator, Plus, Minus,
  Shield, Lock, Check, Upload, X, Mail, Zap,
} from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

const GAMES = [
  { id: 'Minecraft', name: 'Minecraft' },
  { id: 'CounterStrike', name: 'Counter Strike' },
  { id: 'Terraria', name: 'Terraria' },
];

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  description: string;
  recommended?: boolean;
}

const PAYMENT_METHODS: PaymentMethod[] = [
  { id: 'crypto',  name: 'Cripto (USDT)', icon: '/assets/icons/theter.svg', description: 'El método más rápido',  recommended: true },
  { id: 'paypal',  name: 'PayPal',        icon: '/assets/icons/paypal.svg', description: 'Pago vía PayPal' },
  { id: 'visa',    name: 'Visa / Mastercard', icon: '/assets/icons/visa.png', description: 'Tarjeta de crédito/débito' },
  { id: 'binance', name: 'Binance Pay',   icon: '/assets/icons/binance.png', description: 'Pago con Binance' },
  { id: 'zinli',   name: 'Zinli',         icon: '/assets/icons/zinli.jpg', description: 'Pago vía Zinli' },
  { id: 'kontigo', name: 'Kontigo',       icon: '/assets/icons/kontigo.svg', description: 'Pago vía Kontigo' },
];

interface Plan {
  id: string;
  label: string;
  price: number;
}

// =========================================================================
// Calculator (panel lateral — estimación de gasto)
// =========================================================================

function CalculatorPanel() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [hours, setHours] = useState(4);
  const [selectedGame, setSelectedGame] = useState('Minecraft');
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const gameMap: Record<string, string> = {
          Minecraft: 'Minecraft',
          CounterStrike: 'Counter Strike',
          Terraria: 'Terraria',
        };
        const game = gameMap[selectedGame] || selectedGame;
        const res = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/servers/plans/${encodeURIComponent(game)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (res.ok && data.plans) {
          setPlans(data.plans);
          if (data.plans.length > 0) setSelectedPlan(data.plans[0].id);
        }
      } catch (err) {
        console.error('Error loading plans:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [selectedGame]);

  const currentPlan = plans.find((p) => p.id === selectedPlan) || plans[0];
  const costPerHour = currentPlan?.price || 0;
  const totalCost = costPerHour * hours;

  return (
    <section className="sp-panel calc-panel">
      <div className="sp-panel-header">
        <Calculator className="sp-panel-icon" />
        <h2 className="sp-panel-title">Calcula tu gasto</h2>
      </div>
      <div className="sp-panel-body">
        <div className="calc-row">
          <label>Juego</label>
          <select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            className="sp-input"
          >
            {GAMES.map((game) => (
              <option key={game.id} value={game.id}>{game.name}</option>
            ))}
          </select>
        </div>

        <div className="calc-row">
          <label>Plan</label>
          <select
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            className="sp-input"
            disabled={loadingPlans}
          >
            {loadingPlans ? (
              <option>Cargando...</option>
            ) : (
              plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.label} — ${plan.price}/h
                </option>
              ))
            )}
          </select>
        </div>

        <div className="calc-row">
          <label>Horas de juego</label>
          <div className="calc-stepper">
            <button type="button" onClick={() => setHours(Math.max(1, hours - 1))} aria-label="Menos">
              <Minus size={14} />
            </button>
            <span className="calc-stepper-value">{hours}h</span>
            <button type="button" onClick={() => setHours(Math.min(999, hours + 1))} aria-label="Más">
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="calc-result">
          <span className="calc-result-label">
            {hours} {hours === 1 ? 'hora' : 'horas'} de juego
          </span>
          <span className="calc-result-value">${totalCost.toFixed(2)}</span>
        </div>
      </div>
    </section>
  );
}

// =========================================================================
// PaymentForm — contenido del modal según método elegido
// =========================================================================

interface PaymentFormProps {
  methodId: string;
  methodName: string;
  balance: number;
  onSuccess: (title: string, message: string) => void;
  onClose: () => void;
}

function PaymentForm({ methodId, methodName, balance, onSuccess, onClose }: PaymentFormProps) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [generating, setGenerating] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<{
    instructions: string;
    minAmount: number;
    currency: string;
    paymentUrl?: string;
    paymentEmail?: string;
  } | null>(null);

  // Fetch info for non-crypto, non-paypal methods
  useEffect(() => {
    if (methodId === 'crypto' || methodId === 'paypal') {
      setPaymentInfo(null);
      return;
    }
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/payment-info/${methodId}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && data) setPaymentInfo(data);
      } catch (err) {
        console.error('Error loading payment info:', err);
      }
    };
    fetchInfo();
  }, [methodId]);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^(\d*\.?\d{0,2})$/;
    if ((regex.test(value) || value === '') && (parseFloat(value) <= 100 || value === '')) {
      setAmount(value);
      setError('');
    }
  };

  const credits = parseFloat(amount) || 0;
  const isValid = credits > 0;
  const isPaypal = methodId === 'paypal';
  const isCrypto = methodId === 'crypto';
  const minAmount = isCrypto ? 10 : 1;

  // PayPal commission: 45%
  const paypalCommissionRate = 0.45;
  const paypalReceive = isPaypal ? credits * (1 - paypalCommissionRate) : 0;

  const handleBuy = async () => {
    if (!credits || credits <= 0) return;
    if (credits < minAmount) {
      setError(`El mínimo para comprar es $${minAmount} ${isCrypto ? 'USDT' : 'USD'}`);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/create-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: credits, paymentMethod: methodId }),
      });
      const data = await res.json();
      if (res.ok && data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        alert(data.error || 'Error al crear el pago');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('Error al crear el pago');
      setIsLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!email || !isValid) return;
    setGenerating(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/payments/paypal/invoice`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: credits, email }),
      });
      const data = await res.json();
      if (res.ok) {
        onSuccess(
          'Factura Solicitada',
          'Tu factura llegará por PayPal en las próximas horas. Si tienes alguna pregunta, contacta a soporte.'
        );
      } else {
        alert(data.error || 'Error al generar factura');
      }
    } catch (err) {
      console.error('Invoice error:', err);
      alert('Error al generar factura');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileSelect = (file: File | null) => setProofFile(file);

  const handleUploadProof = async () => {
    if (!proofFile) return;
    const formData = new FormData();
    formData.append('file', proofFile);
    formData.append('amount', amount);
    formData.append('paymentMethod', methodId);
    setUploadingProof(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/upload-payment-proof`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUploadSuccess(true);
        onSuccess('Comprobante Subido', 'Tu pago será procesado pronto. Te avisaremos cuando se acredite el saldo.');
      } else {
        alert(data.error || `Error al subir el comprobante (${res.status})`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error al subir el comprobante');
    } finally {
      setUploadingProof(false);
    }
  };

  return (
    <>
      <div className="sp-modal-body">
        {error && (
          <div className="sp-banner sp-banner--error" style={{ borderRadius: 4, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-text">{error}</span>
          </div>
        )}

        <div className="payment-amount-input">
          <span className="payment-amount-currency">{isCrypto ? 'USDT' : 'USD'}</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            className="payment-amount-field"
            autoFocus
          />
        </div>

        {isPaypal && credits > 0 && (
          <p className="payment-amount-info">
            <Shield size={14} /> PayPal cobra 45% de comisión. Recibirás: <strong>${paypalReceive.toFixed(2)} USD</strong>
          </p>
        )}

        {/* Crypto flow */}
        {isCrypto && (
          <div className="payment-info-box">
            <Shield size={16} />
            <div>
              <strong>Pago seguro con OxaPay</strong>
              <p>Serás redirigido a OxaPay para completar el pago con USDT. El mínimo es de {minAmount} USDT.</p>
            </div>
          </div>
        )}

        {/* PayPal flow */}
        {isPaypal && (
          <>
            <p className="payment-step-description">
              Ingresa el monto y tu correo electrónico. Te enviaremos una factura por PayPal.
            </p>
            <div className="sp-form-field" style={{ marginTop: '0.75rem' }}>
              <label htmlFor="paypal-email">Correo electrónico</label>
              <input
                id="paypal-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="sp-input"
              />
            </div>
          </>
        )}

        {/* Standard flow */}
        {!isCrypto && !isPaypal && (
          <>
            {paymentInfo ? (
              <div className="payment-info-box">
                <Shield size={16} />
                <div>
                  <strong>Instrucciones de pago</strong>
                  <p>{paymentInfo.instructions}</p>
                  <p className="payment-info-min">
                    Monto mínimo: <strong>${paymentInfo.minAmount} {paymentInfo.currency}</strong>
                  </p>
                  {paymentInfo.paymentUrl && (
                    <a
                      href={paymentInfo.paymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sp-action-btn sp-action-btn--ghost"
                      style={{ marginTop: '0.5rem' }}
                    >
                      Ir a pagar →
                    </a>
                  )}
                  {paymentInfo.paymentEmail && (
                    <p className="payment-info-email">
                      <Mail size={12} /> {paymentInfo.paymentEmail}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="payment-info-box">
                <p>Cargando información de pago…</p>
              </div>
            )}

            <div className="payment-proof-section">
              <p className="form-label">
                <Upload size={14} /> Sube tu comprobante
              </p>

              <label className="payment-file-label">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  className="sp-form-field"
                  style={{ display: 'none' }}
                />
                <span className="sp-action-btn sp-action-btn--ghost">
                  <Upload size={14} />
                  {proofFile ? 'Cambiar archivo' : 'Seleccionar archivo'}
                </span>
              </label>

              {proofFile && <p className="payment-file-name">📄 {proofFile.name}</p>}

              <button
                type="button"
                className="btn btn-primary"
                disabled={!proofFile || uploadingProof}
                onClick={handleUploadProof}
              >
                {uploadingProof ? 'Subiendo…' : <><Upload size={14} /> Subir comprobante</>}
              </button>

              {uploadSuccess && (
                <p className="payment-upload-success">
                  <Check size={14} /> Comprobante subido correctamente
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="sp-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancelar
        </button>
        {isCrypto && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isValid || isLoading}
            onClick={handleBuy}
          >
            {isLoading ? 'Procesando…' : `Pagar ${amount || '0'} ${isCrypto ? 'USDT' : 'USD'} →`}
          </button>
        )}
        {isPaypal && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isValid || !email || generating}
            onClick={handleGenerateInvoice}
          >
            {generating ? 'Generando…' : 'Generar Factura →'}
          </button>
        )}
      </div>
    </>
  );
}

// =========================================================================
// PaymentMethodCard
// =========================================================================

function PaymentMethodCard({
  method,
  onClick,
}: {
  method: PaymentMethod;
  onClick: () => void;
}) {
  return (
    <button type="button" className="pay-method-card" onClick={onClick}>
      {method.recommended && (
        <span className="pay-method-badge">
          <Zap size={10} /> RÁPIDO
        </span>
      )}
      <div className="pay-method-icon">
        <img src={method.icon} alt={method.name} />
      </div>
      <div className="pay-method-info">
        <span className="pay-method-name">{method.name}</span>
        <span className="pay-method-desc">{method.description}</span>
      </div>
    </button>
  );
}

// =========================================================================
// Main
// =========================================================================

export function BuyCredits() {
  const navigate = useNavigate();
  const { state, isLoading: contextLoading, fetchDashboardData } = useApp();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!state.user) {
      fetchDashboardData().catch(() => navigate('/'));
    }
  }, []);

  useEffect(() => {
    if (!contextLoading && !state.user) navigate('/');
  }, [contextLoading, state.user, navigate]);

  if (contextLoading || (!state.user && !successModal)) {
    return (
      <div className="db-page">
        <div className="db-loading">
          <div className="spinner" />
          <p>Cargando…</p>
        </div>
      </div>
    );
  }

  if (!state.user) return null;

  return (
    <div className="db-page">
      {/* TOPBAR */}
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <Link to="/dashboard" className="sp-topbar-brand">
            <Zap size={16} />
            <span>{APP_NAME}</span>
          </Link>
          <span className="sp-topbar-divider" aria-hidden="true" />
          <span className="sp-topbar-crumb">Recargar saldo</span>
        </div>
        <div className="sp-topbar-right">
          <NotificationsBell />
          <LogoutButton />
        </div>
      </header>

      <main className="db-main">
        {/* HERO */}
        <section className="buy-hero">
          <div className="buy-hero-text">
            <span className="buy-hero-eyebrow">
              <Wallet size={14} /> Tu saldo
            </span>
            <h1 className="buy-hero-balance">
              <span className="buy-hero-currency">$</span>
              {state.balance.toFixed(2).replace('.', ',')}
            </h1>
            <p className="buy-hero-sub">
              Recarga tu cuenta para mantener tus servidores activos. Sin suscripciones, paga solo por lo que usas.
            </p>
          </div>
          <div className="buy-hero-badge">
            <Lock size={14} />
            <span>Pagos seguros</span>
          </div>
        </section>

        {/* GRID */}
        <div className="sp-grid">
          {/* Métodos de pago */}
          <section className="sp-panel sp-col-8 sp-col-sm-12">
            <div className="sp-panel-header">
              <CreditCard className="sp-panel-icon" />
              <h2 className="sp-panel-title">Métodos de pago</h2>
              <span className="sp-pill sp-pill--mono">{PAYMENT_METHODS.length}</span>
            </div>
            <div className="sp-panel-body">
              <div className="pay-methods-grid">
                {PAYMENT_METHODS.map((method) => (
                  <PaymentMethodCard
                    key={method.id}
                    method={method}
                    onClick={() => setSelectedPaymentMethod(method)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Calculadora */}
          <div className="sp-col-4 sp-col-sm-12">
            <CalculatorPanel />
          </div>
        </div>
      </main>

      {/* PAYMENT MODAL */}
      {selectedPaymentMethod && (
        <ModalShell onClose={() => setSelectedPaymentMethod(null)} size="md">
          <ModalHeader
            icon={
              <div className="pay-method-icon pay-method-icon--modal">
                <img src={selectedPaymentMethod.icon} alt={selectedPaymentMethod.name} />
              </div>
            }
            title={`Pagar con ${selectedPaymentMethod.name}`}
            subtitle="Ingresa el monto que quieres recargar"
            onClose={() => setSelectedPaymentMethod(null)}
          />
          <PaymentForm
            methodId={selectedPaymentMethod.id}
            methodName={selectedPaymentMethod.name}
            balance={state.balance}
            onSuccess={(title, message) => {
              setSelectedPaymentMethod(null);
              setSuccessModal({ title, message });
            }}
            onClose={() => setSelectedPaymentMethod(null)}
          />
        </ModalShell>
      )}

      {/* SUCCESS MODAL */}
      {successModal && (
        <ModalShell onClose={() => setSuccessModal(null)} size="sm">
          <ModalHeader
            icon={<Check size={18} />}
            title={successModal.title}
            subtitle="Tu operación fue registrada"
            onClose={() => setSuccessModal(null)}
          />
          <div className="sp-modal-body" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
            <div className="success-icon-circle">
              <Check size={32} />
            </div>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: '1rem' }}>
              {successModal.message}
            </p>
          </div>
          <div className="sp-modal-footer">
            <button type="button" className="btn btn-primary" onClick={() => setSuccessModal(null)}>
              <Check size={14} /> Aceptar
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
